import React, { useState } from "react";
import logo from "../../resource/7.png";
import useFormHandler from "../../Hook/useFormHandler";

const InternalRetreatForm = () => {
  const initialValues = {
    // Access and Personal Information
    access_code: "",
    first_name: "",
    last_name: "",
    age: "",
    religion: "",
    gender: "",
    
    // Address Information
    address: "",
    street_address: "",
    street_address_line_2: "",
    city: "",
    postal_code: "",
    
    // Contact Information
    mobile_number: "",
    whatsapp_number: "",
    email_address: "",
    
    // Meditation Experience
    is_experienced_meditator: "",
    
    // Emergency Contact
    emergency_first_name: "",
    emergency_last_name: "",
    emergency_email: "",
    emergency_relationship: "",
    emergency_mobile_1: "",
    emergency_mobile_2: "",
    
    // Medical History
    has_mental_disorder_history: "",
    has_contagious_disease: "",
    other_health_complications: "",
    
    // Retreat Information
    retreat_no: "",
    attend_full_retreat: "",
    preferred_roommate_name: "",
    number_of_days: "",
    arrival_date: "",
    departure_date: "",
    monastic_status: "",
    
    // Payment and Declaration
    pdf_base64: "",
    pdf_filename: "",
    declaration_full_name: "",
    declaration_date: "",
    
    ip_address: ""
  };

  const validations = {
    // Access and Personal Information
    access_code: {
      required: true,
      minLength: 3,
      message: "Access code is required and must be at least 3 characters",
    },
    first_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "First Name should contain only letters, spaces, or hyphens",
    },
    last_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Last Name should contain only letters, spaces, or hyphens",
    },
    age: {
      required: true,
      pattern: /^\d+$/,
      message: "Age must be a valid number",
      validate: (value) => {
        const numValue = Number(value);
        return (numValue > 0 && numValue < 120) || "Age must be between 1 and 119";
      },
    },
    religion: {
      required: true,
      minLength: 2,
      message: "Religion must be at least 2 characters",
    },
    gender: {
      required: true,
      validate: (value) =>
        ["male", "female", "other"].includes(value) || "Gender must be Male, Female, or Other",
    },
    
    // Address Information
    address: {
      required: true,
      minLength: 5,
      message: "Address must be at least 5 characters long",
    },
    street_address: {
      required: true,
      minLength: 5,
      message: "Street address must be at least 5 characters long",
    },
    street_address_line_2: {
      required: false,
    },
    city: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "City should contain only letters, spaces, or hyphens",
    },
    postal_code: {
      required: true,
      pattern: /^\d{5,10}$/,
      message: "Postal Code should be 5 to 10 digits",
    },
    
    // Contact Information
    mobile_number: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "Mobile Number should be 10 to 15 digits, with optional '+'",
    },
    whatsapp_number: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "WhatsApp Number should be 10 to 15 digits, with optional '+'",
    },
    email_address: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email format",
    },
    
    // Meditation Experience
    is_experienced_meditator: {
      required: true,
      validate: (value) => value !== "" || "Please select your meditation experience level",
    },
    
    // Emergency Contact
    emergency_first_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Emergency Contact First Name should contain only letters, spaces, or hyphens",
    },
    emergency_last_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Emergency Contact Last Name should contain only letters, spaces, or hyphens",
    },
    emergency_email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email format",
    },
    emergency_relationship: {
      required: true,
      minLength: 2,
      message: "Relationship must be at least 2 characters",
    },
    emergency_mobile_1: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "Emergency Mobile 1 should be 10 to 15 digits, with optional '+'",
    },
    emergency_mobile_2: {
      required: false,
      pattern: /^\+?\d{10,15}$/,
      message: "Emergency Mobile 2 should be 10 to 15 digits, with optional '+'",
    },
    
    // Medical History
    has_mental_disorder_history: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },
    has_contagious_disease: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },
    other_health_complications: {
      required: false,
      maxLength: 300,
      message: "Health complications must be under 300 characters",
    },
    
    // Retreat Information
    retreat_no: {
      required: true,
      minLength: 3,
      message: "Retreat number is required",
    },
    attend_full_retreat: {
      required: true,
      validate: (value) => value !== "" || "Please select an option",
    },
    preferred_roommate_name: {
      required: false,
      pattern: /^[A-Za-z\s\-]*$/,
      message: "Name should contain only letters, spaces, or hyphens",
    },
    number_of_days: {
      required: true,
      pattern: /^\d+$/,
      message: "Number of days must be a valid number",
      validate: (value) => {
        const numValue = Number(value);
        return (numValue > 0 && numValue <= 365) || "Number of days must be between 1 and 365";
      },
    },
    arrival_date: {
      required: true,
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      message: "Arrival date must be in YYYY-MM-DD format",
    },
    departure_date: {
      required: true,
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      message: "Departure date must be in YYYY-MM-DD format",
    },
    monastic_status: {
      required: false,
    },
    
    // Declaration
    declaration_full_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Full name should contain only letters, spaces, or hyphens",
    },
    declaration_date: {
      required: true,
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      message: "Date must be in YYYY-MM-DD format",
    },
  };

  const endpoint = '/create-internal-retreat-registrations';

  const transformForApi = (data) => ({
    // Access and Personal Information
    access_code: data.access_code,
    first_name: data.first_name,
    last_name: data.last_name,
    age: Number(data.age),
    religion: data.religion,
    gender: data.gender,
    
    // Address Information
    address: data.address,
    street_address: data.street_address,
    street_address_line_2: data.street_address_line_2,
    city: data.city,
    postal_code: data.postal_code,
    
    // Contact Information
    mobile_number: data.mobile_number,
    whatsapp_number: data.whatsapp_number,
    email_address: data.email_address,
    
    // Meditation Experience
    is_experienced_meditator: data.is_experienced_meditator === "true",
    
    // Emergency Contact
    emergency_first_name: data.emergency_first_name,
    emergency_last_name: data.emergency_last_name,
    emergency_email: data.emergency_email,
    emergency_relationship: data.emergency_relationship,
    emergency_mobile_1: data.emergency_mobile_1,
    emergency_mobile_2: data.emergency_mobile_2,
    
    // Medical History
    has_mental_disorder_history: data.has_mental_disorder_history === "true",
    has_contagious_disease: data.has_contagious_disease === "true",
    other_health_complications: data.other_health_complications,
    
    // Retreat Information
    retreat_no: data.retreat_no,
    attend_full_retreat: data.attend_full_retreat === "true",
    preferred_roommate_name: data.preferred_roommate_name,
    number_of_days: Number(data.number_of_days),
    arrival_date: data.arrival_date,
    departure_date: data.departure_date,
    monastic_status: data.monastic_status || "none",
    
    // Payment and Declaration
    pdf_base64: data.pdf_base64,
    pdf_filename: data.pdf_filename,
    declaration_full_name: data.declaration_full_name,
    declaration_date: data.declaration_date,
    
    ip_address: data.ip_address,
  });

  const {
    formData,
    errors,
    loading,
    handleInputChange,
    handleSubmit,
    setFormData,
  } = useFormHandler({
    initialValues,
    validations,
    endpoint,
    includeIP: true,
    transformFn: transformForApi
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result.split(',')[1];
        setFormData(prev => ({
          ...prev,
          pdf_base64: base64String,
          pdf_filename: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="form-page dark-gold">
      <form onSubmit={handleSubmit}>
        <div className="form-title">
          <img src={logo} alt="Monastery Logo" width={200} />
          <div className="details">
            <h1 className="font-second">Bhaddeka Vihari Monastery Center</h1>
            <h2 className="h4">Internal Retreat Registration Form</h2>
          </div>
        </div>

        <p className="font-p mt-6">
          Please make sure you read the terms and conditions of the Rules and
          Regulations of the Meditation Retreat before submitting this
          application form.
        </p>
        <p className="font-p mt-4">
          The application form must be submitted 10 days prior to your arrival.
          You are required to produce a valid identification upon arrival for
          the retreat.
        </p>
        <p className="font-p mt-4">
          Please provide the access code given to you upon submitting your
          retreat request.
        </p>

        <div className="form-item mt-5">
          <label htmlFor="access_code" className="font-p">
            Access Code*
          </label>
          <input
            type="text"
            id="access_code"
            name="access_code"
            value={formData.access_code}
            onChange={handleInputChange}
          />
          {errors.access_code && <span className="error">{errors.access_code}</span>}
        </div>

        <p className="font-p mt-5">Full Name *</p>
        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
            />
            <label htmlFor="first_name">First Name</label>
            {errors.first_name && <span className="error">{errors.first_name}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
            />
            <label htmlFor="last_name">Last Name</label>
            {errors.last_name && <span className="error">{errors.last_name}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleInputChange}
            />
            <label htmlFor="age">Age*</label>
            {errors.age && <span className="error">{errors.age}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="religion"
              name="religion"
              value={formData.religion}
              onChange={handleInputChange}
            />
            <label htmlFor="religion">Religion*</label>
            {errors.religion && <span className="error">{errors.religion}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Gender*</p>
        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="male"
              name="gender"
              value="male"
              checked={formData.gender === "male"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="male">Male</label>
          </div>
          <div>
            <input
              type="radio"
              id="female"
              name="gender"
              value="female"
              checked={formData.gender === "female"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="female">Female</label>
          </div>
          <div>
            <input
              type="radio"
              id="other"
              name="gender"
              value="other"
              checked={formData.gender === "other"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="other">Other</label>
          </div>
        </div>
        {errors.gender && <span className="error">{errors.gender}</span>}

        <p className="font-p mt-5">Address*</p>
        <div className="form-box1">
          <div className="form-item">
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
            />
            <label htmlFor="address">Full Address</label>
            {errors.address && <span className="error">{errors.address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Street Address*</p>
        <div className="form-box1">
          <div className="form-item">
            <input
              type="text"
              id="street_address"
              name="street_address"
              value={formData.street_address}
              onChange={handleInputChange}
            />
            <label htmlFor="street_address">Street Address</label>
            {errors.street_address && <span className="error">{errors.street_address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Street Address Line 2</p>
        <div className="form-box1">
          <div className="form-item">
            <input
              type="text"
              id="street_address_line_2"
              name="street_address_line_2"
              value={formData.street_address_line_2}
              onChange={handleInputChange}
            />
            <label htmlFor="street_address_line_2">Apartment, Unit, etc. (Optional)</label>
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
            />
            <label htmlFor="city">City*</label>
            {errors.city && <span className="error">{errors.city}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="postal_code"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
            />
            <label htmlFor="postal_code">Postal/Zip Code*</label>
            {errors.postal_code && <span className="error">{errors.postal_code}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="mobile_number"
              name="mobile_number"
              value={formData.mobile_number}
              onChange={handleInputChange}
            />
            <label htmlFor="mobile_number">Mobile Number*</label>
            {errors.mobile_number && <span className="error">{errors.mobile_number}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="whatsapp_number"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleInputChange}
            />
            <label htmlFor="whatsapp_number">WhatsApp Number*</label>
            {errors.whatsapp_number && <span className="error">{errors.whatsapp_number}</span>}
          </div>
        </div>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="email"
              id="email_address"
              name="email_address"
              value={formData.email_address}
              onChange={handleInputChange}
            />
            <label htmlFor="email_address">Email Address*</label>
            {errors.email_address && <span className="error">{errors.email_address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">
          Please indicate if you are a beginner or an experienced meditator*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="beginner">Beginner</label>
            <input
              type="radio"
              id="beginner"
              name="is_experienced_meditator"
              value="false"
              checked={formData.is_experienced_meditator === "false"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="experienced">Experienced meditator</label>
            <input
              type="radio"
              id="experienced"
              name="is_experienced_meditator"
              value="true"
              checked={formData.is_experienced_meditator === "true"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.is_experienced_meditator && <span className="error">{errors.is_experienced_meditator}</span>}

        <h1 className="font-second mt-6">Emergency Contact</h1>
        <p className="font-p mt-5">Full Name*</p>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="emergency_first_name"
              name="emergency_first_name"
              value={formData.emergency_first_name}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_first_name">First Name</label>
            {errors.emergency_first_name && <span className="error">{errors.emergency_first_name}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="emergency_last_name"
              name="emergency_last_name"
              value={formData.emergency_last_name}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_last_name">Last Name</label>
            {errors.emergency_last_name && <span className="error">{errors.emergency_last_name}</span>}
          </div>
        </div>

        <div className="form-box1 mt-4">
          <div className="form-item">
            <input
              type="email"
              id="emergency_email"
              name="emergency_email"
              value={formData.emergency_email}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_email">Email Address*</label>
            {errors.emergency_email && <span className="error">{errors.emergency_email}</span>}
          </div>
        </div>

        <div className="form-box1 mt-4">
          <div className="form-item">
            <input
              type="text"
              id="emergency_relationship"
              name="emergency_relationship"
              value={formData.emergency_relationship}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_relationship">Relationship*</label>
            {errors.emergency_relationship && <span className="error">{errors.emergency_relationship}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="emergency_mobile_1"
              name="emergency_mobile_1"
              value={formData.emergency_mobile_1}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_mobile_1">Mobile Number 1*</label>
            {errors.emergency_mobile_1 && <span className="error">{errors.emergency_mobile_1}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="emergency_mobile_2"
              name="emergency_mobile_2"
              value={formData.emergency_mobile_2}
              onChange={handleInputChange}
            />
            <label htmlFor="emergency_mobile_2">Mobile Number 2 (Optional)</label>
            {errors.emergency_mobile_2 && <span className="error">{errors.emergency_mobile_2}</span>}
          </div>
        </div>

        <h1 className="font-second mt-6">Medical History*</h1>
        <p className="font-p mt-5">
          Kindly answer the following questions truthfully and accurately. You
          are required to answer all of the following questions.
        </p>

        <p className="font-p mt-4">
          Is there any past or present history of mental disorder or illness
          such as panic attack, anxiety, schizophrenia or manic-depression?*
        </p>

        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="mental_yes"
              name="has_mental_disorder_history"
              value="true"
              checked={formData.has_mental_disorder_history === "true"}
              onChange={handleInputChange}
            />
            <label htmlFor="mental_yes">Yes</label>
          </div>
          <div>
            <input
              type="radio"
              id="mental_no"
              name="has_mental_disorder_history"
              value="false"
              checked={formData.has_mental_disorder_history === "false"}
              onChange={handleInputChange}
            />
            <label htmlFor="mental_no">No</label>
          </div>
        </div>
        {errors.has_mental_disorder_history && <span className="error">{errors.has_mental_disorder_history}</span>}

        <p className="font-p mt-4">
          Do you suffer from any contagious diseases like TB and Hepatitis, or
          from any illness that requires constant treatment?*
        </p>

        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="contagious_yes"
              name="has_contagious_disease"
              value="true"
              checked={formData.has_contagious_disease === "true"}
              onChange={handleInputChange}
            />
            <label htmlFor="contagious_yes">Yes</label>
          </div>
          <div>
            <input
              type="radio"
              id="contagious_no"
              name="has_contagious_disease"
              value="false"
              checked={formData.has_contagious_disease === "false"}
              onChange={handleInputChange}
            />
            <label htmlFor="contagious_no">No</label>
          </div>
        </div>
        {errors.has_contagious_disease && <span className="error">{errors.has_contagious_disease}</span>}

        <p className="font-p mt-5">
          Please state any other current health complications and/or
          restrictions, physical or psychological that may and/or will affect
          you during your retreat
        </p>

        <div className="form-item mt-4">
          <textarea
            id="other_health_complications"
            name="other_health_complications"
            value={formData.other_health_complications}
            onChange={handleInputChange}
            rows="4"
            placeholder="Optional - describe any health complications..."
          />
          {errors.other_health_complications && <span className="error">{errors.other_health_complications}</span>}
        </div>

        <h1 className="font-second mt-6">Retreat Attendance Information</h1>

        <div className="form-item mt-5">
          <label htmlFor="retreat_no" className="font-p">
            Retreat No*
          </label>
          <input
            type="text"
            id="retreat_no"
            name="retreat_no"
            value={formData.retreat_no}
            onChange={handleInputChange}
          />
          {errors.retreat_no && <span className="error">{errors.retreat_no}</span>}
        </div>

        <p className="font-p mt-5">Do you plan to attend the full retreat?*</p>
        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="attend_full_yes"
              name="attend_full_retreat"
              value="true"
              checked={formData.attend_full_retreat === "true"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="attend_full_yes">Yes</label>
          </div>
          <div>
            <input
              type="radio"
              id="attend_full_no"
              name="attend_full_retreat"
              value="false"
              checked={formData.attend_full_retreat === "false"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="attend_full_no">No</label>
          </div>
        </div>
        {errors.attend_full_retreat && <span className="error">{errors.attend_full_retreat}</span>}

        <p className="font-p mt-5">
          Do you have a preference for sharing a Kuti during the retreat? If so,
          please provide the full name of your preferred fellow retreatant
          (please ensure that your nominated retreatant is of the same gender)
        </p>

        <div className="form-box1 mt-4">
          <div className="form-item">
            <input
              type="text"
              id="preferred_roommate_name"
              name="preferred_roommate_name"
              value={formData.preferred_roommate_name}
              onChange={handleInputChange}
            />
            <label htmlFor="preferred_roommate_name">Preferred Roommate Name (Optional)</label>
            {errors.preferred_roommate_name && <span className="error">{errors.preferred_roommate_name}</span>}
          </div>
        </div>

        <div className="form-item mt-5">
          <label htmlFor="number_of_days" className="font-p">
            No. of Days you intend to attend the retreat*
          </label>
          <input
            type="number"
            id="number_of_days"
            name="number_of_days"
            value={formData.number_of_days}
            onChange={handleInputChange}
            min="1"
            max="365"
          />
          {errors.number_of_days && <span className="error">{errors.number_of_days}</span>}
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <label htmlFor="arrival_date" className="font-p">Retreat Arrival Date*</label>
            <input
              type="date"
              id="arrival_date"
              name="arrival_date"
              value={formData.arrival_date}
              onChange={handleInputChange}
            />
            {errors.arrival_date && <span className="error">{errors.arrival_date}</span>}
          </div>
          <div className="form-item">
            <label htmlFor="departure_date" className="font-p">Departure Date*</label>
            <input
              type="date"
              id="departure_date"
              name="departure_date"
              value={formData.departure_date}
              onChange={handleInputChange}
            />
            {errors.departure_date && <span className="error">{errors.departure_date}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Section Filled by Monks or Nuns Only</p>
        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="bhikkhu"
              name="monastic_status"
              value="bhikkhu"
              checked={formData.monastic_status === "bhikkhu"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="bhikkhu">Bhikkhu</label>
          </div>
          <div>
            <input
              type="radio"
              id="nun"
              name="monastic_status"
              value="nun"
              checked={formData.monastic_status === "nun"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="nun">Nun</label>
          </div>
        </div>

        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="samanera"
              name="monastic_status"
              value="samanera"
              checked={formData.monastic_status === "samanera"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="samanera">Samanera</label>
          </div>
          <div>
            <input
              type="radio"
              id="anagarika"
              name="monastic_status"
              value="anagarika"
              checked={formData.monastic_status === "anagarika"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="anagarika">Anagarika</label>
          </div>
          <div>
            <input
              type="radio"
              id="none"
              name="monastic_status"
              value="none"
              checked={formData.monastic_status === "none"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="none">None</label>
          </div>
        </div>

        <h1 className="font-second mt-6">Declaration and Indemnity Clauses*</h1>
        <p className="font-p mt-5">
          Please read the following clauses carefully. Should you have any
          questions, please do not hesitate to contact the monastery for
          clarification
        </p>

        <ul className="unorderd-list mt-4 font-p">
          <li>
            I understand that the centre reserves the right to and/or ask me to
            leave should the information, in whole or in part, prove to be
            materially untrue and incorrect.
          </li>
          <li>
            I understand that the centre reserves the right to and/or ask me to
            leave if I don't meet the retreat centre's rules and regulations
            listed here.
          </li>
          <li>
            I shall not hold the centre responsible and/or liable for any mishap
            and untoward occurrences
          </li>
          <li>
            I shall not hold the centre responsible for any physical or mental
            illness that may occur before, during and/or after the retreat
          </li>
          <li>
            I will not hold the centre liable and/or responsible for any items
            lost in its premises
          </li>
          <li>
            You are advised to place your monies, personal valuables and
            traveling documents with the office for safe keeping. Items kept in
            the office is at owner's risk.
          </li>
          <li>
            I understand that the full payment for my retreat application fee
            will not be refunded in the event that I terminate the retreat
            unexpectedly.
          </li>
          <li>
            You are prohibited of taking any illicit drugs, alcohol, smoking or
            vaping during the retreat period. Retreatants are not allow to go
            out of the Meditation Centre during the retreat unless without
            informing the meditation instructor or Monastery senior abbot.
          </li>
          <li>
            No visitors allowed during the retreat. I understand that the centre
            reserves the right to and/or ask me to leave if I don't meet these
            regulations
          </li>
          <li>
            Please refrain from subjecting any form of bullying or harassments,
            failing to maintain a respectful boundary, creating a threatening
            environment and failing to foster comfort and safety of other fellow
            retreatants or retreat staff members
          </li>
          <li>
            You will be held responsible for any damage to property or buildings
            on the premises of the monastery during the retreat period.
          </li>
          <li>
            I understand that priority will be given to retreatants attending
            the full duration of the retreat program
          </li>
          <li>
            Please inform the office if there is any change in your date of
            departure due to an unexpected event.
          </li>
          <li>
            I undertake to abide strictly by the rules of this centre, to
            practice sincerely and conscientiously, and to follow closely the
            instructions of the meditation teacher.
          </li>
        </ul>

        <p className="font-p mt-4 pb-4 color-red">
          Please proceed with the payment of your retreat registration fee of
          $50 here. *
        </p>
        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="file"
              id="payment_receipt"
              name="payment_receipt"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
            />
            <label htmlFor="payment_receipt">Attach online payment receipt here*</label>
            {formData.pdf_filename && (
              <p className="font-p mt-2">Selected file: {formData.pdf_filename}</p>
            )}
          </div>
        </div>

        <p className="font-p mt-5 pb-4">
          I attest that the information given in this application form is true
          and correct. I understand that the centre reserves the right to reject
          my application.
        </p>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="declaration_full_name"
              name="declaration_full_name"
              value={formData.declaration_full_name}
              onChange={handleInputChange}
            />
            <label htmlFor="declaration_full_name">Full Name*</label>
            {errors.declaration_full_name && <span className="error">{errors.declaration_full_name}</span>}
          </div>
          <div className="form-item">
            <input
              type="date"
              id="declaration_date"
              name="declaration_date"
              value={formData.declaration_date}
              onChange={handleInputChange}
            />
            <label htmlFor="declaration_date">Date*</label>
            {errors.declaration_date && <span className="error">{errors.declaration_date}</span>}
          </div>
        </div>

        <button
          type="submit"
          id="submitbtn"
          className="mt-5"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

        <p className="font-p mt-5 pb-4">
          If you find that you have benefited from the practice, and would like
          to contribute to help us maintain the centre and its meditation
          tradition, we would welcome it.
        </p>
      </form>
    </div>
  );
};

export default InternalRetreatForm;